import { Grid, makeStyles } from '@material-ui/core'
import SyncIcon from '@material-ui/icons/Sync'
import actions from 'actions'
import {
  STL_BY_REQUEST_PROVIDERS,
  StlProviders,
  TrackingModes,
  TreatmentSTLsStatuses,
  TxTrackerValidationSteps
} from 'components/Patients/TreatmentTracker/txTrackerConsts'
import SelectInput from 'components/common/SelectInput'
import Tooltip from 'components/common/Tooltip'
import { QuestionMarkIcon } from 'components/common/icons'
import { CONTENT_HEIGHT } from 'consts/patientCard'
import { TreatmentTypes } from 'consts/treatmentConsts'
import { StatusTypes } from '@grin-rnd/grin-api-sdk/dist/Enums/Statuses'
import usePracticeConfiguration from 'hooks/usePracticeConfiguration'
import useTxTrackerValidations from 'hooks/useTxTrackerValidations'
import useUploadSTLs from 'hooks/useUploadSTLs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import DriveUploaderDropzone from './DriveUploaderDropzone'
import InvalidFilesModal from './InvalidFilesModal'
import PreviouslyUploadedStls from './PreviouslyUploadedStls'
import RetentionAwareUploadedStls from './RetentionAwareUploadedStls'
import UploadCompletedModal from './UploadCompletedModal'
import { trackEvent } from 'utils/analyticsUtils'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'

const useStyles = makeStyles({
  container: {
    height: CONTENT_HEIGHT,
    padding: '30px 50px 10px 5%',
    overflowY: 'auto'
  },
  modeTitle: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: 12
  },
  recommendationContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  recommendation: {
    background: '#ECEFFF',
    borderRadius: 10,
    height: '100%',
    width: '95%',
    marginRight: 5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  uploadedSTLsTitleContainer: {
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'space-between'
  },
  requestInProgress: {
    color: '#FFA34D',
    fontWeight: 'bold',
    fontSize: 13,
    display: 'flex'
  },
  anotherUploadNote: {
    marginTop: 10,
    color: '#FFA34D',
    fontWeight: 'bold'
  }
})

const internalProviders = [
  StlProviders.AlignersOtherFullPlan,
  StlProviders.AlignersOtherStartEnd,
  StlProviders.BracesOther
]

const TxTrackerSetup = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [isUploaderComponentLoading, setIsUploaderComponentLoading] = useState(false)
  const [isValidationInProgress, setIsValidationInProgress] = useState(false)

  const {
    id: treatmentId,
    patientId,
    type: treatmentType,
    txTrackerStls,
    txTrackerRetentionStls,
    currentStatus
  } = useSelector(state => state.treatmentReducer.treatment)

  const patientStatusType = useMemo(() => currentStatus?.status?.type, [currentStatus])

  const { ownerAppFeatureFlags } = usePracticeConfiguration()
  const practiceFFs = useSelector(state => state.multiPracticeReducer.practiceFFs)
  const hasRetentionTracker = ownerAppFeatureFlags?.treatmentTrackerRetention || practiceFFs?.treatmentTrackerRetention

  // The upload is routed by the patient's current status: Retention status routes to the
  // Retention reference stack, anything else to the active plan.
  const isRetentionMode = !!hasRetentionTracker && patientStatusType === StatusTypes.Retention
  // Explicitly Active rather than undefined, so an unresolved mode can never read as the
  // active default in gates that branch on it.
  const trackingMode = isRetentionMode ? TrackingModes.Retention : TrackingModes.Active

  const { status: requestSTLsStatus } = useSelector(state => state.txTrackerReducer.requestSTLs)

  const { runTxTrackerValidations, ValidationErrors, providerOptions } = useTxTrackerValidations({
    treatmentType,
    trackingMode,
    txTrackerStls,
    patientId,
    setIsValidationInProgress
  })

  const {
    handleRequestProviderSTLs,
    invalidFiles,
    setInvalidFiles,
    provider,
    setProvider,
    validationError,
    setValidationError,
    isUploadCompletedOpen,
    setIsUploadCompletedOpen,
    treatmentStlsStatus,
    stls,
    existingStlsProvider,
    DROPZONE_CONTAINER_ID,
    manualProvider,
    setManualProvider,
    isMissingProviderName
  } = useUploadSTLs({
    setIsUploaderComponentLoading,
    runTxTrackerValidations,
    ValidationErrors,
    patientId,
    treatmentId,
    treatmentType,
    txTrackerStls,
    trackingMode
  })

  const modeTitle = useMemo(() => {
    if (!hasRetentionTracker) {
      return null
    }

    if (isRetentionMode) {
      return t('dialogs.patientInfo.txTrackerSetup.titleRetention')
    }

    return treatmentType === TreatmentTypes.Braces
      ? t('dialogs.patientInfo.txTrackerSetup.titleActiveBraces')
      : t('dialogs.patientInfo.txTrackerSetup.titleActiveAligners')
  }, [hasRetentionTracker, isRetentionMode, treatmentType, t])

  const recommendationText = useMemo(() => {
    // The per-provider recommendations describe how to extend an active plan; a Retention
    // reference is exactly one upper and one lower STL from any provider.
    if (isRetentionMode) {
      return t('dialogs.patientInfo.txTrackerSetup.recommendationRetention')
    }

    switch (provider) {
      case StlProviders['3M']:
        if (treatmentType === TreatmentTypes.Aligners) {
          return t('dialogs.patientInfo.txTrackerSetup.recommendationMoreThan5')
        } else {
          return t('dialogs.patientInfo.txTrackerSetup.uploadFirstAndLastOnly')
        }
      case StlProviders.Align:
        return t('dialogs.patientInfo.txTrackerSetup.uploadFirstAndLastOnly')
      case StlProviders.Angel:
        return t('dialogs.patientInfo.txTrackerSetup.uploadFirstAndLastOnly')
      case StlProviders.KLOwen:
        return t('dialogs.patientInfo.txTrackerSetup.uploadFirstAndLastOnly')
      case StlProviders.Spark:
        return ''
      case StlProviders.uLab:
        return t('dialogs.patientInfo.txTrackerSetup.recommendationMoreThan5')
      case StlProviders.Tune:
        return t('dialogs.patientInfo.txTrackerSetup.recommendationMoreThan5')
      case StlProviders.Jooylign:
        return t('dialogs.patientInfo.txTrackerSetup.recommendationMoreThan5')
      case StlProviders.Other: {
        if (treatmentType === TreatmentTypes.Aligners) {
          return t('dialogs.patientInfo.txTrackerSetup.recommendationMoreThan5')
        }

        return t('dialogs.patientInfo.txTrackerSetup.uploadFirstAndLastOnly')
      }
      default:
        return ''
    }
  }, [provider, t, treatmentType, isRetentionMode])

  const handleChangeProvider = useCallback(
    async ({ value: providerName }) => {
      const { validationError } = await runTxTrackerValidations({
        provider: providerName,
        step: TxTrackerValidationSteps.SelectProvider,
        trackingMode
      })

      if (!STL_BY_REQUEST_PROVIDERS.includes(providerName) && !validationError) {
        setIsUploaderComponentLoading(true)
      }

      if (validationError) {
        trackEvent('STL Upload Validation Error', {
          validationError,
          provider: providerName,
          treatmentType,
          trackingMode
        })
      }

      setValidationError(validationError)
      setProvider(providerName)
    },
    [treatmentType, trackingMode, setProvider, runTxTrackerValidations, setValidationError]
  )
  const handleManualProviderChanged = useCallback(e => setManualProvider(e.target.value), [setManualProvider])

  useEffect(() => () => dispatch(actions.clearProviderStlsRequestStatus()), [dispatch])

  return (
    <div className={classes.container}>
      <InvalidFilesModal isOpen={!!invalidFiles} invalidFiles={invalidFiles} onClose={() => setInvalidFiles(null)} />
      <UploadCompletedModal isOpen={isUploadCompletedOpen} onClose={() => setIsUploadCompletedOpen(false)} />
      <Grid container direction="column">
        {modeTitle && (
          <Grid item>
            <div className={classes.modeTitle}>{modeTitle}</div>
          </Grid>
        )}
        <Grid item>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <SelectInput
                selectStyle={{ fontSize: 14, fontWeight: 500, marginTop: -2 }}
                showArrow={true}
                dropdownClassName={classes.dropdown}
                style={{ bright: true, thick: true }}
                value={provider}
                options={providerOptions
                  .sort((a, b) => a.label.localeCompare(b.label))
                  // A Retention set may come from any provider; the active plan's provider
                  // lock applies only to uploads that extend the active plan.
                  .filter(option =>
                    isRetentionMode || !existingStlsProvider ? true : option.value === existingStlsProvider
                  )}
                keepErrorContainerWhenInactive={false}
                placeholder={t('dialogs.patientInfo.txTrackerSetup.selectProviderDropdown')}
                onChange={handleChangeProvider}
                disabled={patientStatusType === 'invited'}
              />
            </Grid>
            {(internalProviders.includes(provider) || (isRetentionMode && provider === StlProviders.Other)) && (
              <Grid item>
                <Grid container alignItems="center">
                  <Grid item>Provider name:</Grid>
                  <Grid item>
                    <input type="text" value={manualProvider} onChange={handleManualProviderChanged} />
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={6} className={classes.recommendationContainer}>
              {provider && !internalProviders.includes(provider) && (
                <>
                  <div className={classes.recommendation}>{recommendationText}</div>
                  <Tooltip
                    placement={'right'}
                    value={t('dialogs.patientInfo.txTrackerSetup.recommendationTooltip')}
                    variant="dark"
                  >
                    <div>
                      <QuestionMarkIcon fontSize="inherit" />
                    </div>
                  </Tooltip>
                </>
              )}
            </Grid>
            <Grid item xs={4} className={classes.uploadedSTLsTitleContainer}>
              <div>{t('dialogs.patientInfo.txTrackerSetup.uploadedSTLs')}</div>
              {treatmentStlsStatus === TreatmentSTLsStatuses.PendingFilesTransfer && (
                <div className={classes.requestInProgress}>
                  <SyncIcon style={{ fontSize: 16, marginRight: 4 }} />
                  {t('dialogs.patientInfo.txTrackerSetup.requestInProgress')}
                </div>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={1} style={{ height: 300 }}>
            <Grid item xs={8} style={{ height: '100%' }}>
              <DriveUploaderDropzone
                dropzoneContainerId={DROPZONE_CONTAINER_ID}
                isUploaderComponentLoading={isUploaderComponentLoading}
                provider={provider}
                providerOptions={providerOptions}
                onRequestProviderSTLs={handleRequestProviderSTLs}
                requestSTLsStatus={requestSTLsStatus}
                validationError={validationError}
                isValidationInProgress={isValidationInProgress}
                patientStatusType={patientStatusType}
                isMissingProviderName={isMissingProviderName}
              />
            </Grid>
            <Grid item xs={4} style={{ height: '100%' }}>
              {hasRetentionTracker ? (
                <RetentionAwareUploadedStls
                  stls={stls}
                  treatmentType={treatmentType}
                  provider={existingStlsProvider}
                  txTrackerRetentionStls={txTrackerRetentionStls}
                  isRetentionStatus={isRetentionMode}
                />
              ) : (
                <PreviouslyUploadedStls stls={stls} treatmentType={treatmentType} provider={existingStlsProvider} />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {treatmentStlsStatus === TreatmentSTLsStatuses.PendingFilesTransfer && (
            <DazzedParagraph14 className={classes.anotherUploadNote}>
              {t('dialogs.patientInfo.txTrackerSetup.anotherUploadNote')}
            </DazzedParagraph14>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

export default TxTrackerSetup
